import React, { useState, useEffect, useRef, useCallback } from 'react';

export interface NovaCoin3DProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'reactor';
  className?: string;
  animate?: boolean;
  interactive?: boolean;
  tiltX?: number;
  tiltY?: number;
  speed?: 'slow' | 'normal' | 'fast';
  onClick?: () => void;
}

export const NovaCoin3D: React.FC<NovaCoin3DProps> = ({
  size = 'md',
  className = '',
  animate = true,
  interactive = false,
  tiltX = 0,
  tiltY = 0,
  speed = 'normal',
  onClick,
}) => {
  const sizeMap = {
    xs: 22,
    sm: 30,
    md: 42,
    lg: 58,
    xl: 86,
    '2xl': 120,
    reactor: 148,
  };

  const dim = sizeMap[size] || 42;
  const edgeThickness = size === 'xs' || size === 'sm' ? 2 : size === 'md' ? 3.5 : 5;

  // Local interactive pointer parallax
  const [localTilt, setLocalTilt] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Speed variations
  const spinDuration = speed === 'fast' ? '6s' : speed === 'slow' ? '18s' : '11s';

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const deltaX = (e.clientX - centerX) / (rect.width / 2);
    const deltaY = (e.clientY - centerY) / (rect.height / 2);
    // Clamp to -14deg to 14deg
    setLocalTilt({
      x: Math.max(-14, Math.min(14, -deltaY * 14)),
      y: Math.max(-14, Math.min(14, deltaX * 14)),
    });
  }, [interactive]);

  const handleMouseLeave = useCallback(() => {
    if (!interactive) return;
    setIsHovered(false);
    setLocalTilt({ x: 0, y: 0 });
  }, [interactive]);

  const handleMouseEnter = useCallback(() => {
    if (!interactive) return;
    setIsHovered(true);
  }, [interactive]);

  // Combined tilts (external props + local interactive hover)
  const effectiveTiltX = tiltX !== 0 ? tiltX : localTilt.x;
  const effectiveTiltY = tiltY !== 0 ? tiltY : localTilt.y;

  // Render high-fidelity Coin Face SVG
  const renderCoinFace = (isBack = false) => (
    <svg
      viewBox="0 0 140 140"
      width={dim}
      height={dim}
      className="w-full h-full select-none pointer-events-none"
      style={{ filter: isBack ? 'brightness(0.92)' : 'none' }}
    >
      <defs>
        {/* Metallic Gold Outer Bevel Gradient */}
        <linearGradient id={`goldBevel-${size}-${isBack ? 'b' : 'f'}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fffdf0" />
          <stop offset="15%" stopColor="#fef08a" />
          <stop offset="40%" stopColor="#ffd700" />
          <stop offset="65%" stopColor="#d97706" />
          <stop offset="85%" stopColor="#92400e" />
          <stop offset="100%" stopColor="#451a03" />
        </linearGradient>

        {/* Polished Rim Edge Gradient */}
        <linearGradient id={`rimEdge-${size}`} x1="0%" y1="50%" x2="100%" y2="50%">
          <stop offset="0%" stopColor="#ffd700" />
          <stop offset="25%" stopColor="#fffbeb" />
          <stop offset="50%" stopColor="#b45309" />
          <stop offset="75%" stopColor="#fef08a" />
          <stop offset="100%" stopColor="#78350f" />
        </linearGradient>

        {/* Deep Royal Purple & Violet Chamber Core */}
        <radialGradient id={`reactorPlate-${size}`} cx="40%" cy="36%" r="68%">
          <stop offset="0%" stopColor="#3b1d75" />
          <stop offset="35%" stopColor="#241050" />
          <stop offset="70%" stopColor="#120730" />
          <stop offset="100%" stopColor="#060216" />
        </radialGradient>

        {/* Luminous Electric Cyan Crystal Core */}
        <linearGradient id={`cyanCore-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f0fdfa" />
          <stop offset="25%" stopColor="#00f2fe" />
          <stop offset="65%" stopColor="#0284c7" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>

        {/* Specular Light Reflection Sweep */}
        <linearGradient id={`specSheen-${size}`} x1="0%" y1="0%" x2="100%" y2="80%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.8" />
          <stop offset="28%" stopColor="#ffffff" stopOpacity="0.15" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="85%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* 1. Base Beveled Coin Rim (Outer Ring) */}
      <circle
        cx="70"
        cy="70"
        r="66"
        fill={`url(#goldBevel-${size}-${isBack ? 'b' : 'f'})`}
        stroke="#fef08a"
        strokeWidth="1.8"
      />

      {/* 2. Precision Laser Milling Notches (Edge Tread) */}
      <circle
        cx="70"
        cy="70"
        r="59"
        fill="none"
        stroke="#ffd700"
        strokeWidth="2.2"
        strokeDasharray="3.2 4.2"
        opacity="0.9"
      />

      {/* 3. Deep Recessed Violet Reactor Core Plate */}
      <circle
        cx="70"
        cy="70"
        r="54"
        fill={`url(#reactorPlate-${size})`}
        stroke="#5b21b6"
        strokeWidth="1.5"
      />

      {/* 4. Concentric Energy Wave Tracers */}
      <circle cx="70" cy="70" r="46" fill="none" stroke="#00f2fe" strokeWidth="1.2" opacity="0.65" />
      <circle cx="70" cy="70" r="39" fill="none" stroke="#ec4899" strokeWidth="1" strokeDasharray="5 3" opacity="0.55" />
      <circle cx="70" cy="70" r="33" fill="none" stroke="#ffd700" strokeWidth="0.8" opacity="0.45" />

      {/* 5. Central Faceted NOVA Mining Emblem */}
      <g transform="translate(70, 70)">
        {/* Outer 8-Point Faceted Gold Pickaxe Star */}
        <polygon
          points="0,-34 7,-10 34,0 7,10 0,34 -7,10 -34,0 -7,-10"
          fill={`url(#goldBevel-${size}-${isBack ? 'b' : 'f'})`}
          stroke="#fffbeb"
          strokeWidth="0.8"
          style={{ filter: 'drop-shadow(0 0 6px rgba(255,215,0,0.65))' }}
        />

        {/* Secondary 45° Interlocking Micro-Flares */}
        <polygon
          points="0,-22 5,-6 22,0 5,6 0,22 -5,6 -22,0 -5,-6"
          transform="rotate(45)"
          fill="#d97706"
          opacity="0.85"
        />

        {/* Central Glowing Electric Cyan Diamond Core */}
        <polygon
          points="0,-18 10,-5 18,0 10,5 0,18 -10,5 -18,0 -10,-5"
          fill={`url(#cyanCore-${size})`}
          stroke="#ecfeff"
          strokeWidth="1"
          style={{ filter: 'drop-shadow(0 0 10px rgba(0,242,254,0.95))' }}
        />

        {/* Center Sparkle Quantum Prism */}
        <circle cx="0" cy="0" r="4.8" fill="#ffffff" />
        <circle cx="0" cy="0" r="2.2" fill="#00f2fe" />
      </g>

      {/* 6. Dynamic Specular Highlights & Reflection Sheen */}
      <path
        d="M 22 70 A 48 48 0 0 1 70 22"
        fill="none"
        stroke={`url(#specSheen-${size})`}
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M 32 70 A 38 38 0 0 1 70 32"
        fill="none"
        stroke="#ffffff"
        strokeWidth="1.8"
        strokeLinecap="round"
        opacity="0.6"
      />
    </svg>
  );

  return (
    <div
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      className={`relative inline-flex items-center justify-center select-none ${
        interactive ? 'cursor-pointer' : ''
      } ${className}`}
      style={{
        width: dim,
        height: dim,
        perspective: 900,
      }}
    >
      {/* Soft Ambient Radial Halo behind Coin */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none -z-10"
        style={{
          background: 'radial-gradient(circle, rgba(245,158,11,0.3) 0%, rgba(124,58,237,0.2) 50%, transparent 75%)',
          filter: `blur(${Math.max(6, dim * 0.16)}px)`,
          transform: 'scale(1.25)',
        }}
      />

      {/* Spatial 3D Assembly Anchor */}
      <div
        className={`w-full h-full relative ${animate ? 'spatial-coin-anim' : ''}`}
        style={{
          transformStyle: 'preserve-3d',
          transform: `rotateX(${effectiveTiltX}deg) rotateY(${effectiveTiltY}deg) ${
            isHovered ? 'scale3d(1.08, 1.08, 1.08)' : 'scale3d(1, 1, 1)'
          }`,
          transition: isHovered
            ? 'transform 0.12s ease-out'
            : 'transform 0.45s cubic-bezier(0.25, 1, 0.5, 1)',
          animationDuration: spinDuration,
        }}
      >
        {/* Layer 1: Front Face */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            transformStyle: 'preserve-3d',
            transform: `translateZ(${edgeThickness}px)`,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          {renderCoinFace(false)}
        </div>

        {/* Layer 2: Extruded Cylindrical Physical Rim Layers (Thickness) */}
        {edgeThickness > 1.5 && (
          <>
            <div
              className="absolute inset-0 w-full h-full rounded-full"
              style={{
                transform: `translateZ(${edgeThickness * 0.5}px)`,
                background: 'linear-gradient(135deg, #d97706 0%, #ffd700 50%, #92400e 100%)',
                opacity: 0.92,
              }}
            />
            <div
              className="absolute inset-0 w-full h-full rounded-full"
              style={{
                transform: 'translateZ(0px)',
                background: 'linear-gradient(135deg, #b45309 0%, #f59e0b 50%, #78350f 100%)',
                opacity: 0.95,
              }}
            />
            <div
              className="absolute inset-0 w-full h-full rounded-full"
              style={{
                transform: `translateZ(-${edgeThickness * 0.5}px)`,
                background: 'linear-gradient(135deg, #78350f 0%, #ffd700 50%, #b45309 100%)',
                opacity: 0.92,
              }}
            />
          </>
        )}

        {/* Layer 3: Back Face (Inverted 180° for true 3D spatial rotation) */}
        <div
          className="absolute inset-0 w-full h-full"
          style={{
            transformStyle: 'preserve-3d',
            transform: `rotateY(180deg) translateZ(${edgeThickness}px)`,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          {renderCoinFace(true)}
        </div>
      </div>
    </div>
  );
};

